import cn from 'utils/cn'
import { AnimatePresence, motion } from 'framer-motion'
import {
  ChangeEvent,
  forwardRef,
  InputHTMLAttributes,
  memo,
  ReactElement,
  Ref,
  useMemo
} from 'react'
import Icon from 'core/components/icons/Icon'
import { IconType } from 'core/components/icons/types'

export interface InputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'prefix'
> {
  /**
   * If true, marks the input as invalid and applies error styles.
   */
  invalid?: boolean
  /**
   * Status indicator for the input (success, error, loading, idle).
   */
  status?: 'success' | 'error' | 'loading' | 'pending' | 'idle'
  /**
   * Icon or element to display as a suffix (right side of input). Overridden by status icons.
   */
  suffix?: `${IconType}` | ReactElement
  /**
   * Icon or element to display as a prefix (left side of input).
   */
  prefix?: `${IconType}` | ReactElement
  /**
   * Visual style variant for the input.
   */
  variant?: 'default' | 'invisible'
  /**
   * Additional class names for the input container.
   */
  className?: string
  /**
   * Format string for custom input formatting.
   */
  format?: string
  /**
   * If true, allows clearing the input value with a button.
   */
  allowClear?: boolean
  /**
   * Additional class names for the select wrapper element.
   */
  wrapperClassName?: string
}

/**
 * Input component supporting custom icons, variants, status indicators, and clear functionality.
 * Can be controlled or uncontrolled, and integrates with forms.
 */
const Input = memo(
  forwardRef(
    (
      {
        variant = 'default',
        className,
        suffix,
        prefix,
        invalid = false,
        status = 'idle',
        value = '',
        disabled,
        allowClear,
        ...props
      }: InputProps,
      ref: Ref<HTMLSpanElement>
    ) => {
      const statusStyles = useMemo(() => {
        return {
          idle:
            variant === 'default'
              ? 'focus-within:outline-primary hover:outline-primary outline-dark-2'
              : 'focus-within:outline-primary hover:outline-primary',
          loading: 'outline-dark-2',
          pending: 'outline-dark-2',
          success: '', // no status style
          error: 'text-danger outline-danger hover:outline-danger'
        }
      }, [variant])

      const variantStyles: Record<typeof variant, string | undefined> = {
        default: '',
        invisible: 'outline-transparent hover:bg-white! bg-transparent!'
      }

      const clearIcon = useMemo(() => {
        return (
          <Icon
            name="close"
            width={9}
            height={9}
            className={cn(
              value
                ? 'cursor-pointer bg-dark-3 hover:bg-dark-4'
                : 'bg-transparent',
              'rounded-full p-px'
            )}
            color="text-white"
            onClick={() =>
              props?.onChange?.({
                target: {
                  value: ''
                }
              } as ChangeEvent<HTMLInputElement>)
            }
          />
        )
      }, [props, value])

      const prefixEl = useMemo(() => {
        if (!prefix) return null
        if (prefix && (prefix as string) in IconType) {
          return (
            <Icon
              name={prefix as IconType}
              width={12}
              height={12}
              className={cn([
                'group-focus-within:text-dark mr-2 whitespace-nowrap',
                statusStyles[
                  status !== 'idle' ? status : invalid ? 'error' : 'idle'
                ]
              ])}
            />
          )
        }

        return (
          <span
            className={cn([
              'group-focus-within:text-dark mr-2 whitespace-nowrap',
              statusStyles[
                status !== 'idle' ? status : invalid ? 'error' : 'idle'
              ]
            ])}
          >
            {prefix}
          </span>
        )
      }, [invalid, prefix, status, statusStyles])

      const suffixEl = useMemo(() => {
        const isCoreIcon = (suffix as string) in IconType

        if (status === 'idle' && suffix && isCoreIcon) {
          return (
            <Icon name={suffix as IconType} size={14} className="mb-px ml-2" />
          )
        }
        if (status === 'idle' && suffix && !isCoreIcon) {
          return <span>{suffix}</span>
        }
        if (status === 'loading' || status === 'pending') {
          return <Icon name="loading" size={14} className="mb-[3px] ml-2" />
        }
        if (status === 'success') {
          return (
            <Icon
              name="check-circle-outline"
              size={14}
              className="mb-[2px] ml-2"
            />
          )
        }
        if (status === 'error') {
          return (
            <Icon
              name="exclamation-mark"
              size={14}
              className="mb-[2px] ml-2 text-white bg-danger rounded-full"
            />
          )
        }

        return null
      }, [suffix, status])
      return (
        <label className="contents">
          <motion.span
            ref={ref}
            className={cn([
              'group flex items-center relative text-default rounded-md outline-1 p-2 overflow-clip min-h-[35px] cursor-text',
              disabled
                ? `bg-dark-8 ${props?.readOnly ? 'text-dark' : 'text-dark-3'} outline-dark-2 cursor-not-allowed pointer-events-none`
                : 'bg-white',
              statusStyles[
                status !== 'idle' ? status : invalid ? 'error' : 'idle'
              ],
              variantStyles[variant],
              className,
              props?.readOnly &&
                props.type !== 'select' &&
                'hover:outline-dark-2',
              props?.readOnly &&
                variant === 'invisible' &&
                'focus-within:outline-transparent! hover:outline-transparent!'
            ])}
          >
            <AnimatePresence initial={false}>{prefixEl}</AnimatePresence>

            <input
              className={cn([
                'outline-hidden border-0 bg-transparent w-full grow text-ellipsis peer leading-5!',
                props.type === 'select' && 'cursor-pointer'
              ])}
              disabled={
                disabled || status === 'loading' || status === 'pending'
              }
              value={value}
              {...props}
            />
            <span
              className={cn([
                'group-focus:text-dark whitespace-nowrap',
                status !== 'error' && !invalid && 'text-dark-3',
                statusStyles[
                  status !== 'idle' ? status : invalid ? 'error' : 'idle'
                ]
              ])}
            >
              <AnimatePresence initial={false} mode="wait">
                {status === 'idle' && allowClear && clearIcon}
                {!allowClear && <>{suffixEl}</>}
              </AnimatePresence>
            </span>
          </motion.span>
        </label>
      )
    }
  )
)

export default Input
